// Чистая логика ползунка силы фронта. Сила — абсолютный перепад температуры на
// расстоянии; капитан думает в °C на фиксированную базу, поэтому диапазон
// фиксированный (в отличие от изотермы, где границы = диапазон температур поля).
//
// Единица базы переключается: °C/100 км (обычные мезомасштабные фронты) и
// °C/10 км (очень резкие фронты — при /10 км тот же ползунок достаёт до порогов
// в 10 раз круче, т.к. 1 °C/10 км = 10 °C/100 км). detectFronts всегда ждёт
// °C/100 км, поэтому наружу отдаём toPer100km.

export type FrontUnit = '100km' | '10km'

export const FRONT_STRENGTH_STEP = 0.5
export const DEFAULT_FRONT_UNIT: FrontUnit = '100km'

interface UnitConfig {
  min: number
  max: number
  default: number
  /** множитель перевода значения этой единицы в °C/100 км. */
  per100km: number
}

const UNIT: Record<FrontUnit, UnitConfig> = {
  // слабее 0.5 — фоновый градиент, а не фронт; сильнее 6 в этих водах редки
  '100km': { min: 0.5, max: 6.0, default: 1.5, per100km: 1 },
  // резкие фронты: 0.5…10 °C/10 км = 5…100 °C/100 км
  '10km': { min: 0.5, max: 10.0, default: 1.0, per100km: 10 },
}

/** Умеренный фронт по умолчанию в °C/100 км (совместимость со старым импортом). */
export const DEFAULT_STRENGTH = UNIT['100km'].default

export function defaultStrength(unit: FrontUnit = DEFAULT_FRONT_UNIT): number {
  return UNIT[unit].default
}

export function frontStrengthBounds(unit: FrontUnit = DEFAULT_FRONT_UNIT): { min: number; max: number } {
  const c = UNIT[unit]
  return { min: c.min, max: c.max }
}

/** Прижать силу к диапазону единицы и к сетке 0.5. */
export function clampStrength(value: number, unit: FrontUnit = DEFAULT_FRONT_UNIT): number {
  const c = UNIT[unit]
  const snapped = Math.round(value / FRONT_STRENGTH_STEP) * FRONT_STRENGTH_STEP
  return Math.min(c.max, Math.max(c.min, snapped))
}

/** Значение в выбранной единице → °C/100 км (то, что ждёт detectFronts). */
export function toPer100km(value: number, unit: FrontUnit = DEFAULT_FRONT_UNIT): number {
  return value * UNIT[unit].per100km
}

/** Перевести отображаемое значение при СМЕНЕ единицы. Пытаемся сохранить
 *  физический порог, затем прижимаем к сетке/диапазону новой единицы. Диапазоны
 *  перекрываются лишь на 5…6 °C/100 км, поэтому вне этой полоски порог заметно
 *  меняется (это by design: /10 км и нужен, чтобы уйти в более резкие фронты). */
export function convertStrength(value: number, from: FrontUnit, to: FrontUnit): number {
  if (from === to) return clampStrength(value, to)
  const per100 = toPer100km(value, from)
  return clampStrength(per100 / UNIT[to].per100km, to)
}
